import { faCaretDown, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select as SC, SelectContent, SelectGroup, SelectValue, cn } from '@playcastdotio/Shadcn';
import clsx from 'clsx';
import { ReactNode, forwardRef, useEffect, useState } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
type LocalProps = {
  className?: string;
  variant?: 'light' | 'dark';
  size?: 'small' | 'large';
  onChange?: (value: string) => void;
  selected?: string;
  data: {
    label: string;
    value: string;
    enabled?: boolean;
    icon?: IconProp;
    hero?: boolean;
  }[];
  placeholder?: ReactNode;
};

const SelectTrigger = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <FontAwesomeIcon icon={faCaretDown} className='h-4 w-4' />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));

const SI = forwardRef<React.ElementRef<typeof SelectPrimitive.Item>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>>(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-4 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  ),
);

const SelectItem = ({
  label,
  icon,
  value,
  selected,
  enabled,
  hero,
}: {
  label: string;
  icon?: IconProp;
  value: string;
  selected: string;
  enabled?: boolean;
  hero?: boolean;
}) => {
  const commonStyles =
    'border-2 focus:bg-inherit focus:text-purple-900 hover:focus:text-black rounded-md hover:focus:border-purple-500 hover:focus:border-dashed';
  const largeStyles = 'text-lg font-["Syne"]';
  const selectedStyles = 'border-purple-900';
  const disabledStyles = 'border-gray-300 text-black focus:text-black hover:text-black cursor-not-allowed';

  return (
    <SI
      value={value}
      className={clsx(
        value === selected
          ? clsx(icon ? selectedStyles : 'border-gray-300', 'text-purple-900')
          : clsx(hero ? 'bg-primary' : '', 'border-gray-300'),
        clsx(icon ? largeStyles : '', commonStyles),
        clsx(enabled === false ? disabledStyles : ''),
      )}
    >
      <div className={clsx(icon ? 'flex items-center gap-x-3' : hero ? 'flex items-center gap-x-1' : '')}>
        {icon ? <FontAwesomeIcon icon={icon} className='h-7 w-7 shrink-0' /> : ''}
        {hero ? <FontAwesomeIcon icon={faStar} className='h-4 w-4 shrink-0 p-0 m-0' /> : ''}
        {label}
      </div>
    </SI>
  );
};

export const Select = (props: LocalProps) => {
  const { className, size = 'small', data, variant = 'dark', placeholder = '' } = props;
  const [selectedValue, setSelectedValue] = useState(props.selected ?? data[0]?.value);

  const onValueChange = (value: string) => {
    if (data.find((item) => item.value === value)?.enabled === false) return;
    setSelectedValue(value);
    props.onChange?.(value);
  };

  useEffect(() => {
    setSelectedValue(props.selected ?? data[0]?.value);
  }, [data, props.selected]);

  return (
    <SC value={selectedValue} onValueChange={onValueChange}>
      <SelectTrigger
        className={cn(
          clsx(
            size === 'small' ? 'text-white' : "h-14 font-['Syne'] font-bold text-lg text-primary",
            variant === 'dark' ? 'bg-gray-800' : 'bg-gray-600',
            'flex gap-x-5 w-full border-none focus:outline-none focus:ring-0 drop-shadow-lg text-left',
          ),
          className,
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className='bg-gray-300 drop-shadow-md' style={{ zIndex: 99999 }}>
        <SelectGroup className='text-black'>
          {data.map((item) => (
            <SelectItem key={item.value} {...item} selected={selectedValue} />
          ))}
        </SelectGroup>
      </SelectContent>
    </SC>
  );
};

